import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { CardView } from "@breeze/shared";
import { useUpdateCard } from "../hooks/useBoard.js";
import { useCurrentUser } from "../store/filters.js";

type Props = {
  card: CardView;
};

export function CardActions({ card }: Props): JSX.Element {
  const update = useUpdateCard();
  const currentUser = useCurrentUser();
  const isMine = card.assignedUser === currentUser;
  const isArchived = card.archivedAt !== null;
  const hasReminder = card.reminderAt !== null;
  // Manual archives (no reminder set) shouldn't expose the reminder button —
  // setting one would silently re-archive an already-archived card and the
  // unarchive flow would get confusing. Spec calls this out as out-of-scope.
  const reminderDisabled = isArchived && !hasReminder;
  const [reminderOpen, setReminderOpen] = useState(false);

  const patch = (p: Parameters<typeof update.mutate>[0]["patch"]): void => {
    update.mutate({ cardId: card.cardId, patch: p });
  };

  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5">
      <button
        onClick={() =>
          patch({ priority: card.priority === 1 ? 0 : 1 })
        }
        className={`rounded-md border px-2.5 py-1 text-[12px] uppercase tracking-wider text-ink-50 transition-colors ${
          card.priority === 1
            ? "border-slack-yellow bg-slack-yellow/20"
            : "border-ink-500 hover:border-slack-yellow hover:bg-ink-700"
        }`}
      >
        <span className="mr-1">⚡</span>
        {card.priority === 1 ? "Priority" : "Prioritise"}
      </button>

      <button
        onClick={() =>
          patch({ assignedUser: isMine ? null : currentUser })
        }
        className={`rounded-md border px-2.5 py-1 text-[12px] uppercase tracking-wider text-ink-50 transition-colors ${
          isMine
            ? "border-slack-aubergineLight bg-slack-aubergine/25"
            : "border-ink-500 hover:border-slack-aubergineLight hover:bg-ink-700"
        }`}
      >
        {isMine ? "✓ Mine" : card.assignedUser ? `Take from ${card.assignedUser}` : "Assign to me"}
      </button>

      <button
        onClick={() => !reminderDisabled && setReminderOpen(true)}
        disabled={reminderDisabled}
        title={
          reminderDisabled
            ? "Unarchive the card before setting a reminder."
            : hasReminder
            ? `Reminder set for ${formatLong(card.reminderAt!)} — click to change.`
            : "Snooze this card until a date+time. It will return flashing for everyone."
        }
        className={`rounded-md border px-2.5 py-1 text-[12px] uppercase tracking-wider text-ink-50 transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
          hasReminder
            ? "border-slack-yellow bg-slack-yellow/20"
            : "border-ink-500 hover:border-slack-yellow hover:bg-ink-700"
        }`}
      >
        <span className="mr-1">⏰</span>
        {hasReminder
          ? `Reminder ${formatShort(card.reminderAt!)}`
          : "Set Reminder"}
      </button>

      <button
        onClick={() => patch({ archive: !isArchived })}
        className={`rounded-md border px-2.5 py-1 text-[12px] uppercase tracking-wider text-ink-50 transition-colors ${
          isArchived
            ? "border-slack-aubergineLight bg-slack-aubergine/25"
            : "border-ink-500 hover:border-slack-aubergineLight hover:bg-ink-700"
        }`}
      >
        {isArchived ? "Unarchive" : "Archive"}
      </button>

      {update.isError ? (
        <span className="text-[12px] text-slack-pink">update failed</span>
      ) : null}

      <SetReminderModal
        open={reminderOpen}
        onClose={() => setReminderOpen(false)}
        card={card}
        onSubmit={(iso) => patch({ reminderAt: iso })}
        onClear={() => patch({ reminderAt: null })}
        busy={update.isPending}
      />
    </div>
  );
}

// "Tue 5 May 13:00" — short label for the button when a reminder is set.
function formatShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const date = d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  const time = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${date} ${time}`;
}
function formatLong(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

// ── SET REMINDER MODAL ─────────────────────────────────────────────────────
// Single date+time picker locked to 15-minute steps, defaulted to tomorrow
// 09:00 (or the existing reminderAt when editing). Modal styling mirrors the
// AddContact modal in Slideout.tsx so it feels native to the slide-out.

type ModalProps = {
  open: boolean;
  onClose: () => void;
  card: CardView;
  onSubmit: (iso: string) => void;
  onClear: () => void;
  busy: boolean;
};

function SetReminderModal({
  open,
  onClose,
  card,
  onSubmit,
  onClear,
  busy,
}: ModalProps): JSX.Element {
  const existing = card.reminderAt;
  const [value, setValue] = useState<string>("");

  // Reset the input when the modal opens so re-opens don't show stale state.
  useEffect(() => {
    if (!open) return;
    setValue(existing ? toLocalDatetimeInput(existing) : defaultLocalDatetime());
  }, [open, existing]);

  // ISO of the typed value, or null if the input is empty / unparseable.
  const targetIso = useMemo<string | null>(() => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }, [value]);

  const isFuture = useMemo<boolean>(() => {
    if (!targetIso) return false;
    return new Date(targetIso).getTime() > Date.now();
  }, [targetIso]);

  const canSubmit = isFuture && !busy;

  function handleSubmit(): void {
    if (!canSubmit || !targetIso) return;
    onSubmit(targetIso);
    onClose();
  }
  function handleClear(): void {
    onClear();
    onClose();
  }

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            key="scrim"
            className="fixed inset-0 z-[85] bg-ink-950/70"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            key="dialog"
            className="fixed inset-0 z-[85] flex items-center justify-center p-4"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
          >
            <div
              className="w-full max-w-md rounded-xl border border-ink-700 bg-ink-800 shadow-drag"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="flex items-center justify-between border-b border-ink-700 px-4 py-3">
                <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                  Set reminder
                </div>
                <button
                  onClick={onClose}
                  className="rounded-md border border-ink-500 px-2 py-0.5 text-[12px] text-ink-50 hover:border-ink-400"
                >
                  Close
                </button>
              </header>
              <div className="space-y-3 px-4 py-4 text-[13px]">
                <p className="text-ink-200">
                  By setting a reminder the card will be archived until the
                  reminder time has passed, then it returns flashing for the
                  whole team.
                </p>
                <label className="block">
                  <span className="mb-0.5 block text-[11px] uppercase tracking-[0.14em] text-ink-200">
                    Date / time
                  </span>
                  <input
                    type="datetime-local"
                    step={900}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    className="h-9 w-full rounded-md border border-ink-500 bg-ink-900 px-2 text-[13px] text-ink-50 focus:border-slack-yellow focus:outline-none"
                  />
                </label>
                {targetIso && !isFuture ? (
                  <div className="rounded-md border border-slack-pink/50 bg-slack-pink/10 px-2 py-1 text-[12px] text-slack-pink">
                    Pick a future date/time.
                  </div>
                ) : null}
              </div>
              <footer className="flex items-center justify-between border-t border-ink-700 px-4 py-3">
                {existing ? (
                  <button
                    onClick={handleClear}
                    disabled={busy}
                    className="rounded-md border border-ink-500 px-3 py-1 text-[12px] uppercase tracking-wider text-ink-50 hover:border-slack-pink hover:bg-slack-pink/10 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Clear reminder
                  </button>
                ) : (
                  <span />
                )}
                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className="rounded-md border border-slack-yellow bg-slack-yellow/20 px-4 py-1 text-[13px] font-medium text-ink-50 hover:bg-slack-yellow/30 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {busy ? "Saving…" : "Set reminder"}
                </button>
              </footer>
            </div>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
}

// `<input type="datetime-local">` requires `YYYY-MM-DDTHH:MM` in LOCAL time.
// Browsers will reject ISO/UTC strings. These two helpers convert in both
// directions while staying in the user's local zone.
function toLocalDatetimeInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number): string => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}
function defaultLocalDatetime(): string {
  const t = new Date();
  t.setDate(t.getDate() + 1);
  t.setHours(9, 0, 0, 0);
  return toLocalDatetimeInput(t.toISOString());
}
